//ApplicationSelect.tsx
import React, { useState, useEffect } from 'react';
import { getApplications } from '../../../../service/HomePage/ApplicationsService';  // データリクエストメソッドを導入
import { ApplicationType } from './ApplicationType ';
import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Tooltip } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Avatar from '@mui/material/Avatar';
import theme from '../../../../style/theme';
import { iconMap } from "./iconMap";
import { useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';

/**
 * 申請書一覧コンポーネント
 * - 申請種別ごとにアコーディオン形式で申請書一覧を表示する
 * - 各申請書ボタンをクリックすると申請フォーム画面に遷移する
 */
export default function ControlledAccordions() {

  // 展開されているアコーディオンパネルのID
  const [expanded, setExpanded] = useState<string | false>(false);
  const [applicationsData, setApplicationsData] = useState<ApplicationType[]>([]);
  const navigate = useNavigate();

  // 初期表示時にサーバーから申請書データを取得する
  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const data = await getApplications();
        setApplicationsData(data);
      } catch (error) {
        console.error("Error fetching applications:", error);
      }
    };

    fetchApplications();
  }, []);

  // アコーディオンパネルの展開状態を切り替える
  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  // 申請種別（type）ごとに申請書・アイコン・テーマをグループ化する
  const groupedData = applicationsData.reduce((acc, item) => {
    acc[item.type] = {
      applications: item.applications,
      icon: item.theme.icon,
      buttons: item.theme.buttons,
    };
    return acc;
  }, {} as Record<string, { applications: { id: string; name: string; description: string }[]; icon: any; buttons: any }>);

  return (
    <Box>
      <Card variant="elevation"
        sx={{
          py: 1, boxShadow: 2, // 影効果
          borderRadius: 1, //枠の角を丸くさせる
          overflow: 'auto',
          // maxHeight:'337px',
          maxHeight: '350px',
          background: theme.palette.secondary.main
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center'
          }}>
          <Typography variant="h2" sx={{ px: 2, pb: 1 }}>申請書一覧</Typography>

        </Box>
        <Divider />

        {applicationsData.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              利用可能な申請書がありません
            </Typography>
          </Box>
        ) : (
          <>
            {Object.keys(groupedData).map((type) => {
              const { applications, icon, buttons } = groupedData[type];
              const IconComponent = iconMap[icon.name] || iconMap["MoreHorizRoundedIcon"];

              return (

                <Accordion key={type} expanded={expanded === type} onChange={handleChange(type)}
                  disableGutters
                  sx={{
                    m: 1, boxShadow: 1,
                    '&:before': {
                      display: 'none',
                    },

                  }}

                >
                  {/* パネルタイトル */}
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${type}-content`} id={`${type}-header`}
                    sx={{
                      bgcolor: theme.palette.common.white,
                      p: 2,
                    }}
                  >

                    <Box display="flex" alignItems="center">
                      <Avatar
                        sx={{
                          bgcolor: icon.backgroundColor,
                          width: 20,
                          height: 20,
                          mr: 1,
                          border: `2px solid ${icon.borderColor || icon.backgroundColor}`,
                        }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <IconComponent
                            style={{
                              color: icon.color,
                              width: 15,
                              height: 15,
                            }} />
                        </Box>
                      </Avatar>
                      <Typography variant="h3">{type}</Typography>
                    </Box>
                  </AccordionSummary>

                  {/* パネル内容：申請書ボタンリスト */}
                  <AccordionDetails>
                    <Box
                      sx={(theme: Theme) => ({
                        width: "100%",
                        maxHeight: '227px',
                        overflow: 'auto',
                        bgcolor: theme.palette.secondary.main,
                        p: 0.5,
                        mt: `-${theme.spacing(1)}`,
                        mb: 1
                      })}
                    >

                      <Grid container spacing={0.6}>
                        {applications.map((app) => (
                          <Grid size={12} key={app.id}>
                            <Tooltip
                              title={
                                <>
                                  <div>{app.name}</div>
                                  <div>{app.description}</div>
                                </>
                              }
                              arrow
                            >
                              <Button
                                onClick={() => navigate(`/CustomForm/${app.id}`)}
                                sx={(theme: Theme) => ({
                                  bgcolor: buttons.color,
                                  color: buttons.fontColor,
                                  width: '100%',
                                  textAlign: 'left',
                                  border: `1px solid ${buttons.borderColor}`,
                                  borderRadius: 1,
                                  py: 0.5,
                                  height: '60px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  justifyContent: 'center',
                                  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    backgroundImage: `linear-gradient(to bottom, ${buttons.color} 85%, ${theme.palette.grey[200]} 100%)`,
                                  },
                                })}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{
                                    mb: 0.5,
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    width: '100%',
                                    color: buttons.fontColor,
                                  }}
                                >
                                  {app.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    color: buttons.fontColor,
                                    width: '100%',
                                  }}
                                >
                                  {app.description}
                                </Typography>
                              </Button>
                            </Tooltip>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </>
        )}
      </Card>
    </Box>
  );
}

